import {
  exact,
  fallback,
  readString,
  segment,
  type V2ConsoleTarget
} from '@/utils/v2-console-target/shared'

const organizationPaths: Record<string, string> = {
  DashboardPage: '',
  OrganizationProfilePage: '/settings/general',
  OrganizationUsagePage: '/settings/usage',
  WorkspaceUsagePage: '/settings/usage',
  CreditsPage: '/settings/credits',
  UsersPage: '/settings/members',
  RbacPage: '/settings/roles',
  AlertListPage: '/settings/alerts',
  MaintenanceNoticeListPage: '/settings/maintenance-notices',
  McpServerPage: '/settings/mcp-server',
  PreviewFeaturesPage: '/settings/previews',
  ServiceAccountsPage: '/settings/service-accounts',
  SecretsPage: '/settings/secrets',
  PaymentPage: '/settings/payment',
  InstancesPage: '/instances',
  CatalogsPage: '/infrastructure/catalogs',
  CreateCatalogPage: '/infrastructure/catalogs/new',
  CloudConnectionsPage: '/infrastructure/cloud-connections',
  CloudEnvironmentProvisionPage: '/infrastructure/cloud-environments/new',
  UnilinkDataMigrationsPage: '/universal-links',
  UnilinkSchemaMigrationsPage: '/universal-links/schemas'
}

type OrganizationTargetContext = {
  routeName: string
  params?: Record<string, unknown>
  query?: Record<string, unknown>
  orgBase: string
  instance?: string
  clusterUid?: string
}

export function resolveOrganizationTarget({
  routeName,
  params,
  query,
  orgBase,
  instance,
  clusterUid
}: OrganizationTargetContext): V2ConsoleTarget | undefined {
  const orgFallback = fallback(orgBase, 'organization')
  const organizationSuffix = organizationPaths[routeName]
  if (organizationSuffix !== undefined) return exact(`${orgBase}${organizationSuffix}`)

  if (routeName === 'MaintenanceNoticeDetailPage') {
    const noticeId = readString(params, 'noticeId')
    return noticeId
      ? exact(`${orgBase}/settings/maintenance-notices/${segment(noticeId)}`)
      : orgFallback
  }

  if (routeName === 'ServiceAccountPage') {
    const name = readString(query, 'name')
    return name ? exact(`${orgBase}/settings/service-accounts/${segment(name)}`) : orgFallback
  }

  if (routeName === 'CatalogDetailPage' || routeName === 'CatalogOverviewPage') {
    const name = readString(params, 'name')
    return name ? exact(`${orgBase}/infrastructure/catalogs/${segment(name)}`) : orgFallback
  }

  if (routeName === 'ClusterMcpConfigPage') {
    return clusterUid
      ? exact(`${orgBase}/settings/cluster-mcp-servers/${segment(clusterUid)}`)
      : orgFallback
  }

  if (routeName === 'CloudEnvironmentsPage' || routeName === 'CloudEnvironmentManagePage') {
    return fallback(`${orgBase}/infrastructure`, 'infrastructure')
  }

  if (routeName === 'UnilinksPage') return exact(`${orgBase}/universal-links`)

  const name = readString(params, 'name')
  const type = readString(params, 'type')
  if (routeName === 'UnilinkSchemaOverviewPage') {
    return name
      ? exact(`${orgBase}/universal-links/schemas/${segment(name)}`)
      : fallback(`${orgBase}/universal-links/schemas`, 'universalLinks')
  }

  if (routeName === 'UnilinkDetailPage' || routeName === 'UnilinkOverviewPage') {
    return name
      ? exact(`${orgBase}/universal-links/${segment(name)}`)
      : fallback(`${orgBase}/universal-links`, 'universalLinks')
  }

  if (routeName === 'UnilinkTopicsPage') {
    return name
      ? fallback(`${orgBase}/universal-links/${segment(name)}`, 'universalLink')
      : fallback(`${orgBase}/universal-links`, 'universalLinks')
  }

  if (routeName === 'UnilinkCreationPage') {
    return exact(`${orgBase}/universal-links${type === 'schema' ? '/schemas/new' : '/new'}`)
  }

  if (routeName === 'UnilinkEditPage') {
    if (!name) return fallback(`${orgBase}/universal-links`, 'universalLinks')
    const suffix = type === 'schema' ? `/schemas/${segment(name)}/edit` : `/${segment(name)}/edit`
    return exact(`${orgBase}/universal-links${suffix}`)
  }

  if (routeName === 'ProductsPage' || (routeName === 'ProvisionPage' && !clusterUid)) {
    return fallback(`${orgBase}/instances/new`, 'instanceCreate')
  }

  if (routeName === 'DeployingPage') {
    return fallback(
      instance ? `${orgBase}/instances/${segment(instance)}` : `${orgBase}/instances`,
      'instance'
    )
  }

  return undefined
}
